import type { CreatedUploadDto, DownloadUrlDto, FileArea, FileRefDto } from '@mexp/shared';
import { ApiError, http, resolveApiUrl } from '@/api/client';

/** PUT with upload progress (fetch cannot report upload progress). */
function putWithProgress(url: string, file: File, headers: Record<string, string>, onProgress?: (pct: number) => void, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    for (const [key, value] of Object.entries(headers)) xhr.setRequestHeader(key, value);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new ApiError(xhr.status, 'UPLOAD_FAILED', 'The upload failed. Please try again.')));
    xhr.onerror = () => reject(new ApiError(0, 'UPLOAD_FAILED', 'The upload was interrupted. Check your connection and try again.'));
    xhr.onabort = () => reject(new ApiError(0, 'UPLOAD_ABORTED', 'Upload cancelled'));
    signal?.addEventListener('abort', () => xhr.abort());
    xhr.send(file);
  });
}

/** Reserve → send bytes straight to storage → confirm. Returns the stored file. */
export async function uploadFile(
  file: File,
  area: FileArea,
  opts: { onProgress?: (pct: number) => void; context?: Record<string, string>; signal?: AbortSignal } = {},
): Promise<FileRefDto> {
  const reserved = await http.post<CreatedUploadDto>('/files/uploads', {
    area,
    fileName: file.name,
    contentType: file.type || undefined,
    sizeBytes: file.size,
    context: opts.context,
  });
  await putWithProgress(resolveApiUrl(reserved.upload.url), file, reserved.upload.headers, opts.onProgress, opts.signal);
  return http.post<FileRefDto>(`/files/${reserved.file.id}/complete`);
}

/**
 * Opens a file in a new tab through a short-lived link. The tab is opened synchronously
 * (inside the click) so pop-up blockers allow it, then pointed at the link.
 */
export async function openFile(fileId: string, disposition?: 'inline' | 'attachment'): Promise<void> {
  const tab = window.open('about:blank', '_blank');
  try {
    const { url } = await http.get<DownloadUrlDto>(`/files/${fileId}/url`, { disposition });
    const target = resolveApiUrl(url);
    if (tab) {
      tab.opener = null;
      tab.location.href = target;
    } else {
      window.location.href = target;
    }
  } catch (error) {
    tab?.close();
    throw error;
  }
}
